import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import { formatBytes, IUsage, UsageService } from '../../services/usage.service';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

const pct = (used: number, cap: number): number => (cap > 0 ? Math.min(100, (used / cap) * 100) : 0);

/** Operator view (SystemAdmin / SaaSOperator): every institution's usage this month, nearest the cap first. */
const PlatformUsagePage: React.FC = () => {
    const toast = useToast();
    const { isSystemAdmin } = useAuth();
    const [rows, setRows] = useState<IUsage[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [backfilling, setBackfilling] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setRows(await UsageService.listAll());
            } catch (error) {
                toast.showApiError(error, 'Failed to load usage');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows
            .filter(r => !needle || (r.institutionName ?? '').toLowerCase().includes(needle))
            .sort((a, b) =>
                Math.max(pct(b.transactionsUsed, b.transactionCap), pct(b.storageUsedBytes, b.storageCapBytes))
                - Math.max(pct(a.transactionsUsed, a.transactionCap), pct(a.storageUsedBytes, a.storageCapBytes)));
    }, [rows, search]);

    const handleBackfill = async () => {
        try {
            setBackfilling(true);
            const result = await UsageService.backfillStorage();
            toast.showSuccess(
                `Storage backfill: ${result.recorded} recorded, ${result.alreadyTracked} already tracked, `
                + `${result.skippedNoInstitution} not attributable`);
            setRows(await UsageService.listAll());
        } catch (error) {
            toast.showApiError(error, 'Storage backfill failed');
        } finally {
            setBackfilling(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="platform.usage.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Usage across institutions</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="platform.usage.filters">
                <TextField
                    label="Search institution"
                    data-help="platform.usage.search"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: '1 1 240px', maxWidth: 360 }}
                />
                {isSystemAdmin && (
                    <Button variant="outlined" onClick={handleBackfill} disabled={backfilling} data-help="platform.usage.backfill">
                        {backfilling ? 'Running...' : 'Backfill storage meter'}
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper} data-help-section="platform.usage.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="platform.usage.col.institution">Institution</TableCell>
                            <TableCell data-help="platform.usage.col.plan">Plan</TableCell>
                            <TableCell data-help="platform.usage.col.transactions">Transactions</TableCell>
                            <TableCell data-help="platform.usage.col.storage">Storage</TableCell>
                            <TableCell data-help="platform.usage.col.mode">Mode</TableCell>
                            <TableCell align="right" data-help="platform.usage.col.overage">Overage (৳)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map(r => (
                            <TableRow key={r.institutionId}>
                                <TableCell>{r.institutionName ?? r.institutionId}</TableCell>
                                <TableCell>{r.planName}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    {r.transactionsUsed.toLocaleString()} / {r.transactionCap.toLocaleString()}
                                    <LinearProgress variant="determinate" value={pct(r.transactionsUsed, r.transactionCap)}
                                        color={r.transactionsUsed >= r.transactionCap ? 'error' : 'primary'} />
                                </TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    {formatBytes(r.storageUsedBytes)} / {formatBytes(r.storageCapBytes)}
                                    <LinearProgress variant="determinate" value={pct(r.storageUsedBytes, r.storageCapBytes)}
                                        color={r.storageUsedBytes >= r.storageCapBytes ? 'error' : 'primary'} />
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={r.overageMode === 'CONTINUE' ? 'Continue' : 'Block'}
                                        color={r.overageMode === 'CONTINUE' ? 'warning' : 'default'} />
                                </TableCell>
                                <TableCell align="right">{r.overageTaka.toLocaleString()}</TableCell>
                            </TableRow>
                        ))}
                        {!loading && visible.length === 0 && (
                            <TableRow><TableCell colSpan={6}>No institutions found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PlatformUsagePage;
